import { Box, List, ListItem, ListItemText, TextField, Typography } from '@mui/material';
import type React from 'react';
import { useState } from 'react';
import { sanitizeInput } from '../../utils/formUtils';
import CopyButton from '../common/CopyButton';

import WizardNavigation from './WizardNavigation';

interface Props {
  onNext: (data: { driveId: string }) => void;
  onBack: () => void;
  activeStep: number;
  siteId: string;
  credentials: {
    driveId: string;
  };
}

const Step6DriveId: React.FC<Props> = ({ onNext, onBack, activeStep, siteId, credentials }) => {
  const [driveId, setDriveId] = useState(credentials.driveId || '');
  const [error, setError] = useState('');

  const handleDriveIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const sanitizedValue = sanitizeInput(e.target.value);
    setDriveId(sanitizedValue);
    if (error) {
      validateDriveId(sanitizedValue);
    }
  };

  const validateDriveId = (value: string) => {
    // Drive IDs are complex base64-like strings. A simple non-empty check is safest.
    if (!value) {
      setError('Drive ID is required.');
      return false;
    } else if (value.length < 50) {
      setError('Please enter a valid Drive ID. It is usually a long string of text.');
      return false;
    } else {
      setError('');
      return true;
    }
  };

  const handleBlur = () => {
    validateDriveId(driveId);
  };

  const handleNext = () => {
    if (validateDriveId(driveId)) {
      onNext({ driveId });
    }
  };

  const graphExplorerUrl = `https://graph.microsoft.com/v1.0/sites/${siteId || '{Your-Site-ID}'}/drives`;

  return (
    <Box sx={{ textAlign: 'left' }}>
      <Typography variant="h6" gutterBottom>
        Step 6: Get the Document Library (Drive) ID
      </Typography>
      <Typography variant="body1" paragraph>
        This is the ID for the specific folder (like “Documents”) within the site.
      </Typography>
      <List dense>
        <ListItem>
          <ListItemText
            primary={
              <span>
                1. Using the Site ID from the previous step, construct the following URL in Graph
                Explorer:
              </span>
            }
          />
        </ListItem>
        <ListItem>
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              bgcolor: 'greyContrast',
              color: 'primary.contrastText',
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider',
              p: 1,
              pl: 2,
            }}
          >
            <Box
              component="code"
              sx={{ fontSize: '0.9rem', display: 'block', wordBreak: 'break-all' }}
            >
              {graphExplorerUrl}
            </Box>
            <Box sx={{ position: 'absolute', top: 0, right: 0 }}>
              <CopyButton textToCopy={graphExplorerUrl} />
            </Box>
          </Box>
        </ListItem>
        <ListItem>
          <ListItemText primary='2. Click "Run query".' />
        </ListItem>
        <ListItem>
          <ListItemText
            primary={
              <span>
                3. The response will list all document libraries on the site. Find “Documents” and
                copy its corresponding <code>id</code> value. This is your Drive ID.
              </span>
            }
          />
        </ListItem>
      </List>
      <TextField
        label="Drive ID"
        value={driveId}
        onChange={handleDriveIdChange}
        onBlur={handleBlur}
        fullWidth
        required
        variant="outlined"
        margin="normal"
        sx={{ mt: 2 }}
        error={!!error}
        helperText={error || 'Example: b!1234567890abcdef1234567890abcdef1234567890abcdef'}
      />
      <WizardNavigation
        activeStep={activeStep}
        onBack={onBack}
        onNext={handleNext}
        isNextDisabled={!driveId || !!error}
      />
    </Box>
  );
};

export default Step6DriveId;
